import { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { TextField } from '@cencosud-cencommerce/paris-uikit';

import { noEmojiRegexExp } from '@/constants';
import { SET_VALUE_OPTIONS } from '@/sections/address/components/AddressForm/Autocomplete/constants';
import type { PlacesAutocompleteResult } from '@/sections/address/components/AddressForm/Autocomplete/interfaces';
import { useInputValueSync } from '@/sections/address/components/AddressForm/Autocomplete/useInputValueSync';
import usePlacesAutocomplete from '@/sections/address/components/AddressForm/Autocomplete/usePlacesAutocomplete';
import { LABEL_FIND_ADDRESS } from '@/sections/address/components/AddressForm/constants';
import { getInputStatus } from '@/sections/address/components/AddressForm/utils';
import type { AddressFormValues } from '@/sections/address/types/AddressFormValues';
import { usePlaceData } from '@/sections/region/GooglePlaceDataContext';

const removeEmojis = (text: string): string => {
  return text.replace(noEmojiRegexExp, '');
};

export const Autocomplete = () => {
  const { control, setValue, trigger, watch } =
    useFormContext<AddressFormValues>();
  const hasCommune = !!watch('commune');

  const { setPlaceData } = usePlaceData();

  const handlePlaceSelect = useCallback(
    (place: PlacesAutocompleteResult) => {
      if (!place) return;

      const streetName = place?.route || '';
      const streetNumber = place?.street_number || '';

      const dontHaveStreetNumber = streetNumber === '';
      if (dontHaveStreetNumber) {
        setValue('_skipResetFields', true, SET_VALUE_OPTIONS);
        setValue('latitude', undefined, SET_VALUE_OPTIONS);
        setValue('longitude', undefined, SET_VALUE_OPTIONS);
        setValue('isAutocomplete', false);
        setValue('streetNumber', undefined, SET_VALUE_OPTIONS);
        setValue('streetName', streetName, SET_VALUE_OPTIONS);
        trigger('streetName');
        return;
      }

      setValue('streetName', place?.address_formatted, SET_VALUE_OPTIONS);
      setValue('streetNumber', streetNumber, SET_VALUE_OPTIONS);
      setValue('latitude', place?.latitude, SET_VALUE_OPTIONS);
      setValue('longitude', place?.longitude, SET_VALUE_OPTIONS);
      trigger('streetName');

      const possibleRegion =
        place?.administrative_area_level_1 ||
        place?.administrative_area_level_2 ||
        place?.stateNameCustom ||
        '';
      const possibleCommune =
        place?.administrative_area_level_3 || place?.locality || '';

      if (possibleRegion && possibleCommune) {
        setPlaceData({
          possibleRegion,
          possibleCommune,
          place
        });
      }
    },
    [setValue, trigger, setPlaceData]
  );

  const handleInputChange = useCallback((value: string) => {
    const cleanValue = removeEmojis(value);
    return cleanValue;
  }, []);

  const { inputRef, isLoaded } = usePlacesAutocomplete(handlePlaceSelect);
  useInputValueSync(inputRef);

  const isAutocompleteDisabled = !isLoaded || !hasCommune;

  return (
    <div>
      <Controller
        control={control}
        name="streetName"
        render={({ field, fieldState, formState }) => (
          <TextField
            {...field}
            aria-label="Buscar dirección"
            autoComplete="new-password"
            disabled={isAutocompleteDisabled}
            helperText={fieldState.error?.message}
            id="autocomplete"
            maxLength={73}
            label={LABEL_FIND_ADDRESS}
            status={getInputStatus(formState, fieldState)}
            type="text"
            ref={(e) => {
              inputRef.current = e;
              field.ref(e);
            }}
            onChange={(e) => {
              const cleanValue = handleInputChange(e.target.value);
              field.onChange(cleanValue);
            }}
          />
        )}
      />

      <Controller
        control={control}
        name="streetNumber"
        render={({ field }) => (
          <input type="hidden" {...field} value={field.value || ''} />
        )}
      />

      <Controller
        control={control}
        name="latitude"
        render={({ field }) => (
          <input type="hidden" {...field} value={field.value || ''} />
        )}
      />

      <Controller
        control={control}
        name="longitude"
        render={({ field }) => (
          <input type="hidden" {...field} value={field.value || ''} />
        )}
      />
    </div>
  );
};

Autocomplete.displayName = 'Autocomplete';

export default Autocomplete;
